import { releaseBody } from '../../lib/infra/responseBody';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { PolyAttemptDiagnosticsPanel } from './PolyAttemptDiagnosticsPanel';
import { useAuthStore } from '../../stores/authStore';
import {
  diagnosticState, subscribeDiagnostics, startBrowserDiagnostics,
  stopBrowserDiagnostics, exportBrowserDiagnostics, clearBrowserDiagnostics,
} from '../../lib/infra/browserDiagnostics';

export function BrowserDiagnosticsPanel() {
  const state = useSyncExternalStore(subscribeDiagnostics, diagnosticState);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const availability = useRef<AbortController | null>(null);
  useEffect(() => () => availability.current?.abort(), []);
  const start = async () => {
    setChecking(true);
    setError('');
    const userId = useAuthStore.getState().user?.id;
    const controller = new AbortController();
    availability.current = controller;
    const timeout = setTimeout(() => controller.abort(), 5_000);
    // Diagnostics never refresh credentials or retry a request. Ordinary auth
    // owns token refresh; an unavailable collector must not add auth traffic.
    const send = (path: string, init: RequestInit = {}) => {
      const auth = useAuthStore.getState();
      if (!auth.token || auth.user?.id !== userId) return Promise.reject(new Error('Session changed'));
      return fetch(path, { ...init, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` } });
    };
    try {
      const res = await send('/api/diagnostics/browser', { signal: controller.signal });
      if (!res.ok) { releaseBody(res); throw new Error('Unavailable'); }
      const { remainingMs, automatic } = await res.json();
      if (controller.signal.aborted || useAuthStore.getState().user?.id !== userId
        || !((automatic === true && remainingMs === null) || (Number.isFinite(remainingMs) && remainingMs > 0))) throw new Error('Unavailable');
      startBrowserDiagnostics(async (sample, signal) => {
        const response = await send('/api/diagnostics/browser', { method: 'POST', signal, body: JSON.stringify(sample) });
        releaseBody(response);
        if (response.status === 401 || response.status === 404) stopBrowserDiagnostics();
        return response.ok;
      }, remainingMs ?? Infinity, automatic === true);
    } catch {
      setError('Support must enable performance capture for this account on this deployment first. Performance capture did not start.');
    } finally { clearTimeout(timeout); setChecking(false); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([exportBrowserDiagnostics()], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'g1-browser-diagnostics.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };
  return (
    <section id="browser-diagnostics" className="bg-card border-border rounded-lg border p-5">
      <h2 className="text-base font-medium">Browser diagnostics</h2>
      <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
        Support can enable memory, page-size and sound-decision checkpoints every 30 seconds for selected accounts.
        Otherwise, manual captures run for up to 30 minutes. No order contents, credentials or page text are collected.
        Checkpoints are sent to this deployment and retained for about 24 hours.
      </p>
      <p className="text-muted-foreground mt-2 text-xs">{state.message} {state.samples > 0 && (state.recovered
        ? `${state.samples} recovered. Prior delivery counts are unavailable.`
        : `${state.samples} ${state.automatic ? 'retained' : 'captured'} · ${state.uploaded} received · ${state.failed} failed`)}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button className="bg-secondary rounded px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50" disabled={checking || (!state.active && state.samples > 0 && !state.automatic)} onClick={state.active ? stopBrowserDiagnostics : () => void start()}>
          {state.active ? 'Stop capture' : checking ? 'Checking availability…' : state.automatic ? 'Resume automatic capture' : 'Start 30-minute capture'}
        </button>
        {state.samples > 0 && <button className="bg-secondary rounded px-3 py-2 text-xs" onClick={download}>Download checkpoints</button>}
        {!state.active && state.samples > 0 && <button className="bg-secondary rounded px-3 py-2 text-xs" onClick={clearBrowserDiagnostics}>Clear checkpoints</button>}
      </div>
      {!state.active && state.samples > 0 && <p className="text-muted-foreground mt-2 text-xs">{state.automatic
        ? 'Resuming keeps the latest 60 checkpoints. Download any older evidence you need first.'
        : 'Download any needed evidence before clearing it to start another capture.'}</p>}
      {error && <p className="mt-2 text-xs text-amber-500" role="status">{error}</p>}
      <PolyAttemptDiagnosticsPanel />
    </section>
  );
}
